import { useEffect, useState } from 'react';
import { PaymentRailsApi } from '@/api/clients/payment-rails/payment-rails.api';

const cachedCatalogByOrg = new Map();
const inflightByOrg = new Map();

export const useGetPaymentRailCatalog = (organizationId) => {
  const [catalog, setCatalog] = useState(
    organizationId ? cachedCatalogByOrg.get(organizationId) || [] : [],
  );
  const [loading, setLoading] = useState(
    !!organizationId && !cachedCatalogByOrg.has(organizationId),
  );
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!organizationId) {
      setCatalog([]);
      setLoading(false);
      return;
    }
    if (cachedCatalogByOrg.has(organizationId)) {
      setCatalog(cachedCatalogByOrg.get(organizationId));
      setLoading(false);
      return;
    }
    if (!inflightByOrg.has(organizationId)) {
      inflightByOrg.set(
        organizationId,
        (async () => {
          try {
            const res = await PaymentRailsApi.getCatalog(organizationId);
            const data = res.data?.data || [];
            cachedCatalogByOrg.set(organizationId, data);
            return data;
          } finally {
            inflightByOrg.delete(organizationId);
          }
        })(),
      );
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await inflightByOrg.get(organizationId);
        if (cancelled) return;
        setCatalog(data);
      } catch (err) {
        if (cancelled) return;
        setError(err);
        console.error('Failed to load payment rail catalog:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  return { catalog, loading, error };
};

export const findCatalogEntry = (catalog, method, subType) =>
  catalog.find(
    (entry) =>
      entry.method === method &&
      (entry.subTypes.length === 0
        ? subType == null
        : entry.subTypes.includes(subType)),
  );

export const getPaymentRailDisplayName = (catalog, method, subType) => {
  const entry = findCatalogEntry(catalog, method, subType);
  if (!entry) return `#${method}${subType ? `/${subType}` : ''}`;
  if (entry.subTypes.length === 0) return entry.name;
  return `${entry.name} (${subType})`;
};

export const expandCatalogOptions = (catalog) => {
  const options = [];
  catalog.forEach((entry) => {
    if (entry.subTypes.length === 0) {
      options.push({
        key: `${entry.method}`,
        method: entry.method,
        subType: null,
        label: entry.name,
        workflows: entry.workflows,
      });
    } else {
      entry.subTypes.forEach((subType) => {
        options.push({
          key: `${entry.method}-${subType}`,
          method: entry.method,
          subType,
          label: `${entry.name} (${subType})`,
          workflows: entry.workflows,
        });
      });
    }
  });
  return options;
};
